import {inject} from '@angular/core';
import {Routes} from '@angular/router';
import {InvoiceForm} from './features/finances/components/invoice-form/invoice-form';
import {CreditNoteForm} from './features/finances/components/credit-note-form/credit-note-form';
import {DirectoryHome} from './features/directory/components/directory-home/directory-home';
import {DocComponents} from './doc-components/doc-components';
import {Login} from './auth/login/login';
import {Home} from './home/home';
import {authGuard} from './auth/auth.guard';
import {
  customerProductGuard,
  documentGuard,
  financesGuard,
  licenceServerGuard,
  peppolGuard,
  permissionsManagementGuard,
  productGuard,
  settingsGuard,
  timesheetGuard,
  todoGuard,
  userManagementGuard
} from './auth/permission.guard';
import {unsavedChangesGuard} from './guards/unsaved-changes.guard';
import {TimesheetHome} from './features/timesheets/components/timesheet-home/timesheet-home';
import {TimesheetsSummary} from './features/timesheets/components/timesheets-summary/timesheets-summary';
import {CallStatisticHome} from './features/call-statistics/components/call-statistic-home/call-statistic-home';
import {SettingsSummary} from './features/settings/components/settings-summary/settings-summary';
import {DashboardSummary} from './features/dashboard/components/dashboard-home/dashboard-home.component';
import {TodoSettings} from './features/settings/components/todo-settings/todo-settings';
import {TimesheetSettings} from './features/settings/components/timesheet-settings/timesheet-settings';
import {TodoHome} from './features/todos/components/todo-home/todo-home';
import {ResetPassword} from './auth/reset-password/reset-password';
import {NoPage} from './no-page/no-page';
import {DocumentHome} from './features/documents/document-home/document-home';
import {RouteUri} from './constants/route-uri';
import {CurrentUserService} from './services/current-user.service';
import {FinancesHome} from './features/finances/components/finances-home/finances-home';
import {InvoiceHome} from './features/finances/components/invoice-home/invoice-home';
import {DirectDebitSummary} from './features/finances/components/direct-debit-summary/direct-debit-summary';
import {PeppolHome} from './features/finances/components/peppol-home/peppol-home';
import {CreditNoteSummary} from './features/finances/components/credit-note-summary/credit-note-summary';
import {
  AccountingExportSummary
} from './features/finances/components/accounting-export-summary/accounting-export-summary';
import {ProductHome} from './features/products/components/product-home/product-home';
import {ProductsSummary} from './features/products/components/products-summary/products-summary';
import {LicencePlanHome} from './features/products/components/licence-plan-home/licence-plan-home';
import {FeatureHome} from './features/products/components/feature-home/feature-home';
import {PluginHome} from './features/products/components/plugin-home/plugin-home';
import {AccountingSettings} from './features/settings/components/accounting-settings/accounting-settings';
import {
  AccountingSettingsPeriods
} from './features/settings/components/accounting-settings/components/accounting-periods/accounting-settings-periods';
import {
  AccountingAccountHome
} from './features/settings/components/accounting-settings/components/accounting-accounts/components/accounting-account-home/accounting-account-home';
import {
  AccountingSettingsTaxes
} from './features/settings/components/accounting-settings/components/accounting-taxes/accounting-settings-taxes';
import {MailTemplateSettings} from './features/settings/components/mail-template-settings/mail-template-settings';
import {PermissionsSettings} from './features/settings/components/permissions-settings/permissions-settings';
import {UserSettings} from './features/settings/components/user-settings/user-settings';
import {DirectoryCompanies} from './features/directory/components/directory-companies/directory-companies';
import {DirectoryContacts} from './features/directory/components/directory-contacts/directory-contacts';
import {CompanyFormHome} from './features/directory/components/company-form-home/company-form-home';
import {ContactFormHome} from './features/directory/components/contact-form-home/contact-form-home';
import {CustomerProducts} from './features/directory/components/customer-products/customer-products';
import {CustomerBilling} from './features/directory/components/customer-billing/customer-billing';
import {
  CustomerLicenceServers
} from './features/directory/components/customer-licence-servers/customer-licence-servers';

export const routes: Routes = [
  {
    path: RouteUri.AUTH,
    component: Login
  },
  {
    path: RouteUri.RESET_PASSWORD,
    component: ResetPassword
  },
  {
    path: '',
    component: Home,
    canActivate: [authGuard],
    children: [
      {
        path: '',
        redirectTo: RouteUri.DASHBOARD,
        pathMatch: 'full',
      },
      {
        path: RouteUri.DASHBOARD,
        component: DashboardSummary,
      },
      {
        path: RouteUri.INVOICE_FORM,
        component: InvoiceForm
      },
      {
        path: RouteUri.INVOICE_FORM + '/:id',
        component: InvoiceForm
      },
      {
        path: RouteUri.CREDIT_NOTE_FORM,
        component: CreditNoteForm
      },
      {
        path: RouteUri.CREDIT_NOTE_FORM + '/edit/:id',
        component: CreditNoteForm
      },
      {
        path: RouteUri.CREDIT_NOTE_FORM + '/:invoiceId',
        component: CreditNoteForm
      },
      {
        path: RouteUri.FINANCES,
        component: FinancesHome,
        canActivate: [financesGuard],
        children: [
          {
            path: RouteUri.INVOICES,
            component: InvoiceHome
          },
          {
            path: RouteUri.CREDIT_NOTES,
            component: CreditNoteSummary
          },
          {
            path: RouteUri.DIRECT_DEBIT,
            component: DirectDebitSummary
          },
          {
            path: RouteUri.ACCOUNTING_EXPORT,
            component: AccountingExportSummary
          },

          {
            path: RouteUri.PEPPOL,
            component: PeppolHome,
            canActivate: [peppolGuard]
          },
          {
            path: '',
            redirectTo: RouteUri.INVOICES,
            pathMatch: 'full'
          },
        ]
      },
      {
        path: RouteUri.DOCUMENTATION,
        component: DocComponents
      },
      {
        path: RouteUri.DIRECTORY,
        component: DirectoryHome,
        children: [
          {
            path: '',
            redirectTo: RouteUri.DIRECTORY_COMPANIES,
            pathMatch: 'full'
          },
          {
            path: RouteUri.DIRECTORY_COMPANIES,
            component: DirectoryCompanies
          },
          {
            path: RouteUri.DIRECTORY_CONTACTS,
            component: DirectoryContacts
          },
        ]
      },
      {
        path: RouteUri.DIRECTORY_FORM,
        children: [
          {
            path: '',
            redirectTo: RouteUri.DIRECTORY_FORM_COMPANY,
            pathMatch: 'full'
          },
          {
            path: RouteUri.DIRECTORY_FORM_COMPANY,
            component: CompanyFormHome,
            canDeactivate: [unsavedChangesGuard]
          },
          {
            path: RouteUri.DIRECTORY_FORM_CONTACT,
            component: ContactFormHome,
            canDeactivate: [unsavedChangesGuard]
          },
          {
            path: RouteUri.DIRECTORY_FORM_CONTACT + '/:id',
            component: ContactFormHome,
            canDeactivate: [unsavedChangesGuard]
          },
          {
            path: ':id',
            component: CompanyFormHome,
            canDeactivate: [unsavedChangesGuard],
            children: [
              {
                path: '',
                redirectTo: RouteUri.DIRECTORY_FORM_DETAILS,
                pathMatch: 'full'
              },
              {
                path: RouteUri.DIRECTORY_FORM_DETAILS,
                children: []
              },
              {
                path: RouteUri.DIRECTORY_FORM_PRODUCTS,
                component: CustomerProducts,
                canActivate: [customerProductGuard]
              },
              {
                path: RouteUri.DIRECTORY_FORM_BILLING,
                component: CustomerBilling,
                canActivate: [financesGuard]
              },
              {
                path: RouteUri.DIRECTORY_FORM_LICENCE_SERVERS,
                component: CustomerLicenceServers,
                canActivate: [licenceServerGuard]
              },
            ]
          },
        ]
      },
      {
        path: RouteUri.TIMESHEETS,
        component: TimesheetsSummary,
        canActivate: [timesheetGuard],
        children: [
          {
            path: '',
            redirectTo: RouteUri.TIMESHEETS_LIST,
            pathMatch: 'full'
          },
          {
            path: RouteUri.TIMESHEETS_LIST,
            component: TimesheetHome
          },
          {
            path: RouteUri.CALL_STATISTICS,
            component: CallStatisticHome
          },
        ]
      },
      {
        path: RouteUri.TODOS,
        component: TodoHome,
        canActivate: [todoGuard]
      },
      {
        path: RouteUri.DOCUMENTS,
        component: DocumentHome,
        canActivate: [documentGuard]
      },
      {
        path: RouteUri.PRODUCTS,
        component: ProductsSummary,
        canActivate: [productGuard],
        children: [
          {
            path: '',
            redirectTo: RouteUri.PRODUCTS_LIST,
            pathMatch: 'full'
          },
          {
            path: RouteUri.PRODUCTS_LIST,
            component: ProductHome
          },
          {
            path: RouteUri.LICENCE_PLANS,
            component: LicencePlanHome
          },
          {
            path: RouteUri.FEATURES,
            component: FeatureHome
          },
          {
            path: RouteUri.PLUGINS,
            component: PluginHome
          },
        ]
      },
      {
        path: RouteUri.SETTINGS,
        component: SettingsSummary,
        canActivate: [settingsGuard],
        children: [
          {
            path: '',
            // Land on the first tab the user is allowed to see. Mail templates only
            // require settings access, so they are always a valid fallback.
            redirectTo: () => {
              const currentUserService = inject(CurrentUserService);
              if (currentUserService.hasTodoAccess()) return RouteUri.TODO_SETTINGS;
              if (currentUserService.hasTimesheetAccess()) return RouteUri.TIMESHEET_SETTINGS;
              if (currentUserService.hasFinanceAccess()) return RouteUri.ACCOUNTING_SETTINGS;
              return RouteUri.MAIL_TEMPLATE_SETTINGS;
            },
            pathMatch: 'full'
          },
          {
            path: RouteUri.TODO_SETTINGS,
            component: TodoSettings,
            canActivate: [todoGuard]
          },
          {
            path: RouteUri.TIMESHEET_SETTINGS,
            component: TimesheetSettings,
            canActivate: [timesheetGuard]
          },
          {
            path: RouteUri.ACCOUNTING_SETTINGS,
            component: AccountingSettings,
            canActivate: [financesGuard],
            children: [
              {
                path: '',
                redirectTo: RouteUri.ACCOUNTING_CODES,
                pathMatch: 'full'
              },
              {
                path: RouteUri.ACCOUNTING_CODES,
                component: AccountingAccountHome
              },
              {
                path: RouteUri.ACCOUNTING_PERIODS,
                component: AccountingSettingsPeriods
              },
              {
                path: RouteUri.ACCOUNTING_TAXES,
                component: AccountingSettingsTaxes
              },
            ]
          },
          {
            path: RouteUri.MAIL_TEMPLATE_SETTINGS,
            component: MailTemplateSettings
          },
          {
            path: RouteUri.PERMISSIONS_SETTINGS,
            component: PermissionsSettings,
            canActivate: [permissionsManagementGuard]
          },
          {
            path: RouteUri.USER_SETTINGS,
            component: UserSettings,
            canActivate: [userManagementGuard]
          },
        ]
      },
    ],
  },
  {
    path: '**',
    component: NoPage
  },
];
